import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { AuthState, UserProfile } from '@/middleware/AuthMiddleware';

// UserProfile interface is now imported from AuthMiddleware

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string, rememberMe: boolean) => Promise<any>;
  signUp: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  logout: () => Promise<{ success: boolean; error: string | null }>;
  refreshSession: () => Promise<void>;
  updateAuthState: (authState: AuthState) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  isAdmin: false,
  isAuthenticated: false,
  isLoading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  logout: async () => ({ success: false, error: null }),
  refreshSession: async () => {},
  updateAuthState: () => {},
});

// Lightweight profile fetcher — uses raw fetch instead of supabase client
// because the supabase JS client hangs on the production domain.
async function loadProfileForUser(userId: string): Promise<UserProfile | null> {
  try {
    const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL || 'https://khwvlnkrtrwvfbnnjsrc.supabase.co';
    const SUPABASE_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY || '';
    const tokenRaw = localStorage.getItem('supabase.auth.token');
    const accessToken = tokenRaw ? JSON.parse(tokenRaw).access_token : null;

    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/profiles?select=id,email,full_name,role,stripe_customer_id&id=eq.${userId}`,
      {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${accessToken || SUPABASE_KEY}`,
        },
      }
    );
    if (!res.ok) return null;
    const rows = await res.json();
    return rows[0] || null;
  } catch {
    return null;
  }
}

// Read the same cached authState that AuthMiddleware writes on login.
// This lets AuthContext hydrate with a real user from render 0 instead
// of waiting for AuthMiddleware's onAuthStateChange callback to fire.
// Without this, ChatbotList (and any component reading `user?.id`) sees
// null on first render and its `useQuery({ enabled: !!userId })` stays
// permanently disabled — showing skeleton forever.
const readCachedAuthState = (): AuthState => {
  try {
    const raw = localStorage.getItem('authState');
    if (!raw) return { user: null, session: null, profile: null, isAdmin: false, isAuthenticated: false, isLoading: true };
    const parsed = JSON.parse(raw);
    if (parsed?.user?.id) {
      return {
        user: parsed.user,
        session: parsed.session ?? null,
        profile: parsed.profile ?? null,
        isAdmin: !!parsed.isAdmin,
        isAuthenticated: true,
        isLoading: false,
      };
    }
  } catch {
    // Corrupted cache — fall through to default loading state
  }
  return { user: null, session: null, profile: null, isAdmin: false, isAuthenticated: false, isLoading: true };
};

export function AuthProvider({ children }: { children: ReactNode }) {
  // Hydrate from localStorage synchronously on first render (no network
  // call, no effect).
  const [authState, setAuthState] = useState<AuthState>(readCachedAuthState);
  const navigate = useNavigate();

  // Update auth state from middleware (kept for backward compat)
  const updateAuthState = (newAuthState: AuthState) => {
    setAuthState(newAuthState);
  };

  // Subscribe to Supabase auth state changes DIRECTLY. This replaces the
  // old "AuthMiddleware calls onAuthStateChange prop" indirection that
  // caused a render race where ChatbotList saw `user: null` on first
  // paint and its useQuery stayed disabled forever.
  //
  // Now: Supabase fires the SIGNED_IN event the moment the token lands
  // in storage, AuthContext updates immediately, and any consumer with
  // `useQuery({ enabled: !!userId })` re-runs on the next render — no
  // matter what AuthMiddleware is doing in parallel.
  useEffect(() => {
    let cancelled = false;

    // 1. Get the current session immediately on mount. If there's a valid
    //    session in localStorage from a previous tab, this resolves in
    //    a single tick and we never show a loading state.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session?.user) {
        setAuthState((prev) => ({
          ...prev,
          user: session.user,
          session,
          isAuthenticated: true,
          isLoading: false,
        }));
        // Profile fetch is fire-and-forget — never blocks the user from
        // seeing their dashboard. Selects only the columns we read.
        loadProfileForUser(session.user.id).then((profile) => {
          if (cancelled || !profile) return;
          setAuthState((prev) => ({
            ...prev,
            profile,
            isAdmin: profile.role === 'admin',
          }));
        });
      } else {
        setAuthState((prev) => ({ ...prev, isLoading: false }));
      }
    });

    // 2. Subscribe to future auth events (SIGNED_IN, SIGNED_OUT,
    //    TOKEN_REFRESHED, USER_UPDATED). Same flow each time.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (session?.user) {
        setAuthState((prev) => ({
          ...prev,
          user: session.user,
          session,
          isAuthenticated: true,
          isLoading: false,
        }));
        loadProfileForUser(session.user.id).then((profile) => {
          if (cancelled || !profile) return;
          setAuthState((prev) => ({
            ...prev,
            profile,
            isAdmin: profile.role === 'admin',
          }));
        });
      } else {
        setAuthState({
          user: null, session: null, profile: null,
          isAdmin: false, isAuthenticated: false, isLoading: false,
        });
      }
    });

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string, rememberMe: boolean) => {
    console.log('🔐 AuthContext: Attempting sign in with:', { email, hasPassword: !!password });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('🔐 AuthContext: Sign in error:', error);
        throw error;
      }

      console.log('✅ AuthContext: Sign in successful:', data.user?.email);

      // Profile will be loaded automatically by auth state change listener
      return data;

    } catch (error) {
      console.error('🔐 AuthContext: Sign in failed:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    console.log('Attempting sign up with:', { email, hasPassword: !!password });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/portal`
      }
    });
    
    if (error) {
      console.error('Supabase signup error:', error);
      throw error;
    }
    
    console.log('Sign up successful:', data);
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    navigate('/portal/auth');
  };

  const logout = async () => {
    try {
      await signOut();
      // Clear any stored persistence preferences
      localStorage.removeItem('supabase.auth.persistence');
      sessionStorage.removeItem('supabase.auth.persistence');
      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const refreshSession = async () => {
    try {
      console.log('🔐 AuthContext: Manually refreshing session...');
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('🔐 AuthContext: Manual session refresh failed:', error);
        await supabase.auth.signOut();
      } else {
        console.log('✅ AuthContext: Manual session refresh successful');
      }
    } catch (error) {
      console.error('🔐 AuthContext: Manual refresh error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      ...authState,
      signIn,
      signUp,
      signOut,
      logout,
      refreshSession,
      updateAuthState
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 